import React, { ReactNode } from "react";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import { Button } from "@jem-open/jem-ui";
import { MapPin } from "lucide-react";
import GeofenceCircle from "../../time-attendance/components/GeofenceCircle";
import MapCenterSync from "../../time-attendance/components/MapCenterSync";
import { AddressSearch } from "./AddressSearch";
import { GeofenceMapPreview } from "./GeofenceMapPreview";

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

// Cape Town — matches the existing time-attendance site-config map default.
const DEFAULT_CENTER = { lat: -33.9, lng: 18.4 };
const DEFAULT_ZOOM = 14;
const MAP_ID = "site-config-geofence-map";

/** Round to 6 decimal places (~0.1 m) so coordinate strings stay tidy. */
const round6 = (n: number): string => String(Math.round(n * 1e6) / 1e6);

export interface GeofenceMapEditorProps {
  latitude: string;
  longitude: string;
  radiusMeters: string;
  /** Drives the "Effective geofence" label and the static fallback. */
  source: "site" | "override";
  siteName?: string;
  /** Called when the address search, marker drag, or "use current" sets coords. */
  onCoordsChange: (latitude: string, longitude: string) => void;
  /** The latitude / longitude / radius input fields, rendered under the search. */
  children: ReactNode;
  addressLabel?: string;
}

/**
 * Live geofence editor: a Places address search + an interactive Google Map
 * with a draggable marker and a geofence circle, alongside the manual
 * latitude / longitude / radius inputs (passed as `children`).
 *
 * Reuses the dashboard's existing `@vis.gl/react-google-maps` integration
 * (`GeofenceCircle`, `MapCenterSync`) so behaviour matches the Time & Attendance
 * site-config page. When no Google Maps API key is configured it degrades
 * gracefully to the static `GeofenceMapPreview`.
 */
export const GeofenceMapEditor: React.FC<GeofenceMapEditorProps> = ({
  latitude,
  longitude,
  radiusMeters,
  source,
  siteName,
  onCoordsChange,
  children,
  addressLabel,
}) => {
  const latNum = parseFloat(latitude);
  const lngNum = parseFloat(longitude);
  const hasCoords = Number.isFinite(latNum) && Number.isFinite(lngNum);
  const lat = hasCoords ? latNum : DEFAULT_CENTER.lat;
  const lng = hasCoords ? lngNum : DEFAULT_CENTER.lng;
  const radius = parseFloat(radiusMeters) || 0;
  const mapCenter = hasCoords ? { lat, lng } : DEFAULT_CENTER;

  const handleMarkerDrag = (e: google.maps.MapMouseEvent): void => {
    if (e.latLng) {
      onCoordsChange(round6(e.latLng.lat()), round6(e.latLng.lng()));
    }
  };

  const handleUseCurrentLocation = (): void => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      onCoordsChange(
        round6(position.coords.latitude),
        round6(position.coords.longitude)
      );
    });
  };

  // Graceful fallback when the Maps SDK can't load: manual inputs + static map.
  if (!googleMapsApiKey) {
    return (
      <div className="grid gap-md md:grid-cols-2">
        <div className="space-y-md">{children}</div>
        <GeofenceMapPreview
          latitude={hasCoords ? latNum : 0}
          longitude={hasCoords ? lngNum : 0}
          radiusMeters={radius || 100}
          source={source}
          siteName={siteName}
        />
      </div>
    );
  }

  const sourceLabel =
    source === "site"
      ? `Effective geofence — Same as ${siteName ?? "Site"}`
      : "Effective geofence — Custom";

  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <div className="grid gap-md md:grid-cols-2">
        <div className="space-y-md">
          <AddressSearch
            label={addressLabel}
            onSelect={(la, ln) => onCoordsChange(round6(la), round6(ln))}
          />
          {children}
        </div>
        <div className="flex h-full flex-col">
          <div className="mb-xxs flex items-center gap-xxs text-xs text-greyscale-text-caption">
            <MapPin size={12} aria-hidden="true" />
            <span>{sourceLabel}</span>
          </div>
          <div className="relative min-h-[240px] flex-1 overflow-hidden rounded-md border border-greyscale-border">
            <Map
              style={{ width: "100%", height: "100%", minHeight: 240 }}
              defaultCenter={mapCenter}
              defaultZoom={DEFAULT_ZOOM}
              gestureHandling="greedy"
              mapId={MAP_ID}
            >
              <MapCenterSync lat={lat} lng={lng} enabled={hasCoords} />
              {hasCoords && (
                <AdvancedMarker
                  position={{ lat, lng }}
                  draggable
                  onDragEnd={handleMarkerDrag}
                />
              )}
              {hasCoords && radius > 0 && (
                <GeofenceCircle center={{ lat, lng }} radius={radius} />
              )}
            </Map>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="absolute bottom-xs right-xs z-[1] bg-white shadow-md"
              onClick={handleUseCurrentLocation}
            >
              Use current
            </Button>
          </div>
          <div className="mt-xxs text-xs text-greyscale-text-caption">
            {hasCoords
              ? `Centre: ${latNum.toFixed(5)}, ${lngNum.toFixed(
                  5
                )} · Radius: ${radius}m`
              : "Search an address or drop a pin to set the geofence centre."}
          </div>
        </div>
      </div>
    </APIProvider>
  );
};

export default GeofenceMapEditor;
